/**
 * FormShell error-focus plumbing — the WCAG 3.3.1 (Error Identification) +
 * 2.4.3 (Focus Order) wiring that takes the user TO the first invalid field on
 * a failed submit rather than leaving them to hunt for it.
 *
 * Unlike the wizard (`wizard-validation.ts`), whose `field` is always a DOM
 * `id`, a FormShell error's `field` is a looser display / name string: it may
 * match a DOM `id`, a control `name`, or nothing at all. So focus resolution
 * tries, in order: `#field`, `[name="field"]`, then falls back to the first
 * control the form itself flagged `aria-invalid` (the signal every ui-kit
 * control emits inside a `<Field>` / `<FormControl>`). Everything here is pure
 * (JSX-free) so the `node --test` sweep covers the decision table without a
 * DOM; FormShell's render-side wiring (bodyRef, the focus effect, and the
 * clickable summary entries) is pinned by source assertions in
 * `__tests__/form-shell-error-focus.test.ts`.
 */

/** A single FormShell validation issue — the flat shape FormShell accepts. */
export interface FormErrorIssue {
  /** Display / name / id string for the offending field (optional). */
  field?: string;
  message: string;
}

/** CSS.escape a token for use in a `#id` selector, with a manual fallback for
 *  non-DOM runtimes (the `node --test` sweep). */
function escapeForId(token: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(token)
    : token.replace(/["\\]/g, '\\$&');
}

/**
 * Build the ordered CSS-selector candidates for locating the control an error
 * entry belongs to. A `field` that isn't a valid bare id (spaces, dots) would
 * throw as `#…`; CSS.escape keeps it query-safe, and the `[name=…]` form is
 * always valid. Both are attempted before the generic `aria-invalid` fallback,
 * which is always appended so a field-less error still lands the user on the
 * first invalid control. Returns at least the fallback selector.
 */
export function invalidFieldSelectors(field?: string): string[] {
  const selectors: string[] = [];
  if (field && field.length > 0) {
    selectors.push(
      `#${escapeForId(field)}`,
      `[name="${field.replace(/["\\]/g, '\\$&')}"]`,
    );
  }
  selectors.push('[aria-invalid="true"]');
  return selectors;
}

/**
 * The field string focus should target first: the first error that names a
 * field, or `undefined` when no error carries one (focus then falls through to
 * the `aria-invalid` fallback via {@link invalidFieldSelectors}).
 */
export function firstErrorField(
  errors: ReadonlyArray<FormErrorIssue> | undefined,
): string | undefined {
  return errors?.find((e) => e.field && e.field.length > 0)?.field;
}

/**
 * Focus the control the first FormShell error refers to, within `root`. Tries
 * the field-specific selectors first, then the generic first-invalid-control
 * fallback, so the user is TAKEN to the offending input (WCAG 3.3.1 / 2.4.3).
 * Returns true when a control was found and focused. The focus call is guarded
 * for SSR / non-element roots, and a malformed selector is skipped rather than
 * thrown.
 */
export function focusFirstFormError(
  root: { querySelector: (selectors: string) => Element | null } | null,
  errors: ReadonlyArray<FormErrorIssue> | undefined,
): boolean {
  if (!root || !errors || errors.length === 0) return false;
  for (const selector of invalidFieldSelectors(firstErrorField(errors))) {
    let el: Element | null = null;
    try {
      el = root.querySelector(selector);
    } catch {
      el = null;
    }
    if (el && typeof (el as HTMLElement).focus === 'function') {
      (el as HTMLElement).focus();
      return true;
    }
  }
  return false;
}

/**
 * A stable content key for an error list — folds field + message so a second
 * failed submit whose first error changed still re-triggers the focus effect,
 * while an identical re-render does not.
 */
export function errorSignature(
  errors: ReadonlyArray<FormErrorIssue> | undefined,
): string {
  return (errors ?? []).map((e) => `${e.field ?? ''} ${e.message}`).join('');
}
